/**
 * Pure PvP-match logic shared by the Battleship modal ({@link
 * ./battleship-game.tsx}) and the arcade's pending-matches panel ({@link
 * ./arcade-pending-matches.tsx}). Keeping it here — free of React, `~/client`,
 * and the generated runtime graph — lets both surfaces classify a player's
 * active matches the same way, and lets the Japa unit runner exercise the logic
 * directly (see `tests/unit/battleship_pvp_matches.spec.ts`).
 */

/**
 * The subset of `Data.GameMatch` these helpers read. `Data.GameMatch`
 * structurally satisfies it, so callers pass their real matches unchanged while
 * tests can build lightweight fixtures.
 */
export type PvpMatchLike = {
  status: "waiting" | "placing" | "playing" | "finished" | "abandoned" | "declined" | "expired";
  mode: "open" | "challenge";
  challengerId: string;
  opponentId: string | null;
};

/** True when `myId` is the opponent of a still-pending challenge (a reto I owe an answer). */
function isIncomingChallenge(m: PvpMatchLike, myId: string): boolean {
  return m.status === "waiting" && m.mode === "challenge" && myId !== "" && m.opponentId === myId;
}

/** True when the match is an owned `waiting` seat/challenge I can still cancel. */
function isOwnedWaiting(m: PvpMatchLike, myId: string): boolean {
  return m.status === "waiting" && myId !== "" && m.challengerId === myId;
}

/** True when the match is mid-flight (ships being placed, or turns being played). */
function isInProgress(m: PvpMatchLike): boolean {
  return m.status === "placing" || m.status === "playing";
}

/**
 * Pick which of the actor's active matches to adopt, BY PRIORITY, so the correct
 * screen shows when they hold several at once (e.g. a lingering open seat/challenge
 * AND an incoming challenge they still owe an answer to). Adopting the wrong one
 * (their own "searching" seat) would hide the incoming challenge and they'd never
 * accept it. Order:
 *   1. an incoming challenge they must answer (`waiting` + `challenge` + they are
 *      the opponent) — highest, it's the only one blocking someone else,
 *   2. else a resumable game already in progress (`placing`/`playing`),
 *   3. else their own `waiting` seat/challenge (a fallback they can now cancel).
 * Returns null for an empty list. Pure — no side effects.
 */
export function pickActiveMatch<T extends PvpMatchLike>(matches: T[], myId: string): T | null {
  const incoming = matches.find((m) => isIncomingChallenge(m, myId));
  if (incoming) {
    return incoming;
  }
  const resumable = matches.find((m) => isInProgress(m));
  if (resumable) {
    return resumable;
  }
  return matches[0] ?? null;
}

/**
 * Split a player's active matches into the three buckets the pending-matches
 * panel renders. Preserves input order within each bucket. Pure.
 *
 *   - `incoming`   — retos aimed at me, awaiting my accept/decline,
 *   - `outgoing`   — my own `waiting` seats/challenges (reto sent, or open
 *                    matchmaking seat), awaiting the rival / cancelable by me,
 *   - `inProgress` — matches already in placing/playing I can resume.
 *
 * A `waiting` challenge to someone else (I am neither seat) falls into none.
 */
export function categorizePvpMatches<T extends PvpMatchLike>(
  matches: T[],
  myId: string,
): { incoming: T[]; outgoing: T[]; inProgress: T[] } {
  const incoming: T[] = [];
  const outgoing: T[] = [];
  const inProgress: T[] = [];

  for (const m of matches) {
    if (isIncomingChallenge(m, myId)) {
      incoming.push(m);
    } else if (isOwnedWaiting(m, myId)) {
      outgoing.push(m);
    } else if (isInProgress(m)) {
      inProgress.push(m);
    }
  }

  return { incoming, outgoing, inProgress };
}

/**
 * Route NAME the arcade deep-link ("Abrir" in a game alert, and the pending
 * panel) must target. The workspace arcade only mounts on the channels
 * (Internal) welcome screen, so this MUST stay `channels.index` — `chat.index`
 * renders a different page that never reads `?arcade` and strands the user on
 * the external chat instead of the game.
 */
export const ARCADE_SECTION_ROUTE = "channels.index" as const;

/** Query param the arcade section reads on mount to auto-open a game modal. */
export const ARCADE_QUERY_PARAM = "arcade";

/** Append the arcade deep-link intent (`?arcade=<gameKey>`) to a resolved path. */
export function arcadeDeepLinkPath(basePath: string, gameKey: string): string {
  return `${basePath}?${ARCADE_QUERY_PARAM}=${gameKey}`;
}
